/**
 * Calls dashboard contracts (super-admin).
 *
 *   GET /v1/admin/calls/dashboard?range=7d|30d|90d   super-admin
 *
 * A fleet-wide read of in-app audio and video calls. Stream Video is the only
 * source: nothing is stored on our side yet. The backend reads two Stream
 * lists for every Stream app a school can call through (the shared app plus
 * any school-filed app) and joins them per call:
 *
 * - `queryCalls`: every call created, with its creator, members and
 *   `custom.video`. Stream never fills `session` here, so it says nothing about
 *   whether anyone joined.
 * - `queryCallSessionStats`: one row per session that actually ran, with start,
 *   end, participant counts and a quality score.
 *
 * A call with no session row never connected: the callee did not pick up, the
 * caller hung up while ringing, or Stream has not produced the stats yet (a few
 * minutes after the call ends). Stream does not report missed vs declined vs
 * cancelled in these lists, so the dashboard does not either.
 *
 * Stream's call history currently reaches back about 90 days, which bounds the
 * longest range.
 */

import { z } from 'zod'

export const CallsDashboardRangeSchema = z.enum(['7d', '30d', '90d'])
export type CallsDashboardRange = z.infer<typeof CallsDashboardRangeSchema>

export const GetCallsDashboardRequestSchema = z.object({
  range: CallsDashboardRangeSchema.default('30d')
})
export type GetCallsDashboardRequest = z.infer<typeof GetCallsDashboardRequestSchema>

/** `custom.video` on the call; a call without it counts as audio. */
export const CallMediaSchema = z.enum(['audio', 'video'])
export type CallMedia = z.infer<typeof CallMediaSchema>

/** Two members is a direct call; more is a group call. */
export const CallKindSchema = z.enum(['direct', 'group'])
export type CallKind = z.infer<typeof CallKindSchema>

/**
 * - `connected`: a session had two or more participants.
 * - `unanswered`: a session ran but only one person joined (the caller waited alone).
 * - `not_joined`: no session ran (see the file header for what that covers).
 */
export const CallOutcomeSchema = z.enum(['connected', 'unanswered', 'not_joined'])
export type CallOutcome = z.infer<typeof CallOutcomeSchema>

/** The caller's role at their school, from our membership row (Stream's copy when the row is gone). */
export const CallerRoleSchema = z.enum(['faculty', 'staff', 'parent', 'student', 'admin', 'unknown'])
export type CallerRole = z.infer<typeof CallerRoleSchema>

export const CallsDashboardTotalsSchema = z.object({
  calls: z.number().int().nonnegative(),
  connected: z.number().int().nonnegative(),
  unanswered: z.number().int().nonnegative(),
  notJoined: z.number().int().nonnegative(),
  /** connected / calls; null when there are no calls. */
  connectRate: z.number().min(0).max(1).nullable(),
  /** Sum of session wall-clock time (first start to last end) over connected calls. */
  connectedSeconds: z.number().nonnegative(),
  /** Sum of each participant's time in the call, over all sessions. */
  participantSeconds: z.number().nonnegative(),
  avgConnectedSeconds: z.number().nonnegative().nullable(),
  medianConnectedSeconds: z.number().nonnegative().nullable(),
  audio: z.number().int().nonnegative(),
  video: z.number().int().nonnegative(),
  direct: z.number().int().nonnegative(),
  group: z.number().int().nonnegative(),
  uniqueCallers: z.number().int().nonnegative(),
  schools: z.number().int().nonnegative(),
  /** Mean Stream call-quality score (0-100) over connected calls that have one. */
  avgQualityScore: z.number().min(0).max(100).nullable()
})
export type CallsDashboardTotals = z.infer<typeof CallsDashboardTotalsSchema>

/** One UTC day. Every day of the range is present, zero-filled. */
export const CallsDashboardDailyPointSchema = z.object({
  /** `YYYY-MM-DD`, UTC. */
  date: z.string(),
  calls: z.number().int().nonnegative(),
  connected: z.number().int().nonnegative(),
  audio: z.number().int().nonnegative(),
  video: z.number().int().nonnegative(),
  connectedSeconds: z.number().nonnegative()
})
export type CallsDashboardDailyPoint = z.infer<typeof CallsDashboardDailyPointSchema>

export const CallsDashboardSchoolRefSchema = z.object({
  /** `School.publicId`. */
  publicId: z.string(),
  name: z.string()
})
export type CallsDashboardSchoolRef = z.infer<typeof CallsDashboardSchoolRefSchema>

/** One school's calls, keyed by the caller's school. Sorted by calls, descending. */
export const CallsDashboardSchoolRowSchema = z.object({
  /** Null groups calls whose caller resolves to no school. */
  school: CallsDashboardSchoolRefSchema.nullable(),
  calls: z.number().int().nonnegative(),
  connected: z.number().int().nonnegative(),
  connectRate: z.number().min(0).max(1).nullable(),
  connectedSeconds: z.number().nonnegative(),
  avgConnectedSeconds: z.number().nonnegative().nullable(),
  audio: z.number().int().nonnegative(),
  video: z.number().int().nonnegative(),
  uniqueCallers: z.number().int().nonnegative(),
  lastCallAt: z.string()
})
export type CallsDashboardSchoolRow = z.infer<typeof CallsDashboardSchoolRowSchema>

export const CallsDashboardCallerSchema = z.object({
  /** Stream user id = `UsersOnSchools.publicId`. */
  publicId: z.string(),
  name: z.string(),
  role: CallerRoleSchema
})
export type CallsDashboardCaller = z.infer<typeof CallsDashboardCallerSchema>

/**
 * How the caller's mobile app was found. Stream's call carries no app id, so
 * each is a best reading from our rows, not a record of the call itself:
 * - `last_sign_in`: the app the caller's membership last signed in through
 *   (`SchoolUsersOnMobileApps`, newest `lastLoginAt`). Today's value, so it can
 *   differ from the app used at call time if they have switched since.
 * - `school_app`: no sign-in row; the school has exactly one active app.
 * - `main_app`: no sign-in row and the school has no app of its own, so the
 *   caller can only be on the main School Journey app.
 */
export const CallsDashboardAppViaSchema = z.enum(['last_sign_in', 'school_app', 'main_app'])
export type CallsDashboardAppVia = z.infer<typeof CallsDashboardAppViaSchema>

/** The branded mobile app (`MobileApp`) a call was placed from. One app can serve several schools. */
export const CallsDashboardAppRefSchema = z.object({
  /** `MobileApp.publicId`. */
  publicId: z.string(),
  /** The on-device app name, else the store title, else the public id. */
  name: z.string(),
  via: CallsDashboardAppViaSchema
})
export type CallsDashboardAppRef = z.infer<typeof CallsDashboardAppRefSchema>

/** Which Stream app the call lives in: the shared one, or a school-filed one. */
export const CallsStreamAppKindSchema = z.enum(['shared', 'school'])
export type CallsStreamAppKind = z.infer<typeof CallsStreamAppKindSchema>

export const CallsDashboardCallSchema = z.object({
  /** Stream call cid, `type:id`. */
  cid: z.string(),
  createdAt: z.string(),
  /** First session start; null when no session ran. */
  startedAt: z.string().nullable(),
  /** Last session end; null when no session ran. */
  endedAt: z.string().nullable(),
  /** startedAt → endedAt in seconds; null when no session ran. */
  durationSeconds: z.number().nonnegative().nullable(),
  media: CallMediaSchema,
  kind: CallKindSchema,
  memberCount: z.number().int().nonnegative(),
  /** Most participants any session of the call had; null when no session ran. */
  participantCount: z.number().int().nonnegative().nullable(),
  outcome: CallOutcomeSchema,
  /** Stream call-quality score 0-100; null when Stream gave none. */
  qualityScore: z.number().min(0).max(100).nullable(),
  school: CallsDashboardSchoolRefSchema.nullable(),
  caller: CallsDashboardCallerSchema.nullable(),
  /** The caller's mobile app; null when the caller has no membership row or the app is ambiguous. */
  app: CallsDashboardAppRefSchema.nullable(),
  streamApp: CallsStreamAppKindSchema
})
export type CallsDashboardCall = z.infer<typeof CallsDashboardCallSchema>

/** One Stream app the read covered, and whether its read failed. */
export const CallsDashboardSourceSchema = z.object({
  kind: CallsStreamAppKindSchema,
  /** Shared app: `Shared`. School app: the school's name. */
  label: z.string(),
  calls: z.number().int().nonnegative(),
  sessions: z.number().int().nonnegative(),
  /** Set when this app's read failed; its calls are then missing from every figure. */
  error: z.string().nullable(),
  /** True when a page cap stopped the read before the range was covered. */
  truncated: z.boolean()
})
export type CallsDashboardSource = z.infer<typeof CallsDashboardSourceSchema>

/** At most this many calls are listed in `recentCalls`, newest first. */
export const CALLS_DASHBOARD_RECENT_LIMIT = 200

export const CallsDashboardSchema = z.object({
  range: CallsDashboardRangeSchema,
  /** Range start, ISO. */
  from: z.string(),
  /** Range end (the read time), ISO. */
  to: z.string(),
  /** When Stream was read; the backend caches a read for a few minutes. */
  generatedAt: z.string(),
  totals: CallsDashboardTotalsSchema,
  daily: z.array(CallsDashboardDailyPointSchema),
  schools: z.array(CallsDashboardSchoolRowSchema),
  recentCalls: z.array(CallsDashboardCallSchema),
  sources: z.array(CallsDashboardSourceSchema)
})
export type CallsDashboard = z.infer<typeof CallsDashboardSchema>

export const GetCallsDashboardResponseSchema = z.object({
  message: z.string(),
  data: CallsDashboardSchema
})
export type GetCallsDashboardResponse = z.infer<typeof GetCallsDashboardResponseSchema>
